import React, { useEffect, useMemo, useState } from "react"
import { Row, Col, Button, Input } from "reactstrap"
import Select from "react-select"
import BootstrapTable from "react-bootstrap-table-next"
import {
  getLeagues,
  getSpecialMarketCall,
  attachSpecialMarketLeagueCall,
  detachSpecialMarketLeagueCall,
} from "helpers/repository"
import { showErrorMessages, mapDataForDropDown } from "helpers/helpers"
import { showMessage } from "components/Common/Toaster"

const StatusBadge = ({ status }) => (
  <span
    className={`badge badge-soft-${
      status?.toLowerCase() === "active" ? "success" : "secondary"
    } font-size-12`}
  >
    {status}
  </span>
)

export default function SpecialMarketLeaguesPanel({
  specialMarket,
  onChanged,
  onLeaguesCountChange,
}) {
  const [activeLeagues, setActiveLeagues] = useState(
    specialMarket?.active_leagues || []
  )
  const [allLeagues, setAllLeagues] = useState([])
  const [leaguesLoading, setLeaguesLoading] = useState(false)
  const [leaguesRequested, setLeaguesRequested] = useState(false)
  const [leagueSearchInput, setLeagueSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [selectedLeagueOption, setSelectedLeagueOption] = useState(null)
  const [newLeagueStatus, setNewLeagueStatus] = useState("active")
  const [attaching, setAttaching] = useState(false)
  const [removingId, setRemovingId] = useState(null)

  const loadLeaguesOnce = () => {
    if (leaguesRequested) return
    setLeaguesRequested(true)
    setLeaguesLoading(true)
    getLeagues(
      res => {
        setAllLeagues(mapDataForDropDown(res?.data?.data || []))
        setLeaguesLoading(false)
      },
      () => {
        setAllLeagues([])
        setLeaguesLoading(false)
      }
    )
  }

  // Load once in the background so attached-league names can resolve;
  // the dropdown itself never renders more than a handful of options
  // (see dropdownOptions below), so this fetch doesn't block interaction.
  useEffect(() => {
    loadLeaguesOnce()
  }, [])

  const refetchMarket = async () => {
    try {
      const response = await getSpecialMarketCall(specialMarket.id)
      const leagues = response?.data?.data?.active_leagues || []
      setActiveLeagues(leagues)
      onLeaguesCountChange?.(leagues.length)
    } catch (error) {
      showErrorMessages(error)
    }
  }

  const dropdownOptions = useMemo(() => {
    if (!leagueSearchInput || leagueSearchInput.length < 2) return []
    const term = leagueSearchInput.toLowerCase()
    const matches = []
    for (const o of allLeagues) {
      if (o.label?.toLowerCase().includes(term)) {
        matches.push(o)
        if (matches.length >= 50) break
      }
    }
    return matches
  }, [allLeagues, leagueSearchInput])

  const leagueLabelById = useMemo(
    () => new Map(allLeagues.map(o => [String(o.id), o.label])),
    [allLeagues]
  )
  const leagueNameById = id => leagueLabelById.get(String(id))

  const filteredLeagues = useMemo(() => {
    if (!search) return activeLeagues
    const term = search.toLowerCase()
    return activeLeagues.filter(
      l =>
        String(l.league_id).includes(term) ||
        leagueNameById(l.league_id)?.toLowerCase().includes(term)
    )
  }, [activeLeagues, search, leagueLabelById])

  const handleAttach = async () => {
    if (!selectedLeagueOption) return
    setAttaching(true)
    try {
      const response = await attachSpecialMarketLeagueCall(specialMarket.id, {
        league_id: String(selectedLeagueOption.id),
        status: newLeagueStatus,
      })
      showMessage("success", response?.data?.message || "League added")
      setSelectedLeagueOption(null)
      setLeagueSearchInput("")
      await refetchMarket()
      onChanged?.()
    } catch (error) {
      showErrorMessages(error)
    } finally {
      setAttaching(false)
    }
  }

  const handleDetach = async row => {
    setRemovingId(row.id)
    try {
      const response = await detachSpecialMarketLeagueCall(
        specialMarket.id,
        row.league_id
      )
      showMessage("success", response?.data?.message || "League removed")
      await refetchMarket()
      onChanged?.()
    } catch (error) {
      showErrorMessages(error)
    } finally {
      setRemovingId(null)
    }
  }

  const columns = [
    {
      dataField: "league_id",
      text: "League",
      formatter: cell => leagueNameById(cell) || cell,
    },
    {
      dataField: "status",
      text: "Status",
      formatter: cell => <StatusBadge status={cell} />,
    },
    {
      dataField: "row_actions",
      text: "",
      isDummyField: true,
      formatter: (_cell, row) => (
        <Button
          size="sm"
          color="danger"
          outline
          disabled={removingId === row.id}
          onClick={() => handleDetach(row)}
        >
          {removingId === row.id ? "Removing..." : "Remove"}
        </Button>
      ),
    },
  ]

  return (
    <div>
      <Row className="align-items-end mb-3">
        <Col md={6}>
          <label className="form-label">Add League</label>
          <Select
            maxMenuHeight={150}
            options={dropdownOptions}
            inputValue={leagueSearchInput}
            onInputChange={(value, { action }) => {
              if (action === "input-change") setLeagueSearchInput(value)
            }}
            onFocus={loadLeaguesOnce}
            onMenuOpen={loadLeaguesOnce}
            isLoading={leaguesLoading}
            filterOption={null}
            noOptionsMessage={() => {
              if (leaguesLoading) return "Loading leagues…"
              if (leagueSearchInput.length < 2)
                return "Type at least 2 characters to search leagues"
              return "No leagues found"
            }}
            value={selectedLeagueOption}
            onChange={setSelectedLeagueOption}
            isClearable
            classNamePrefix="select2-selection"
          />
        </Col>
        <Col md={3}>
          <label className="form-label">Status</label>
          <Input
            type="select"
            value={newLeagueStatus}
            onChange={e => setNewLeagueStatus(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Input>
        </Col>
        <Col md={3}>
          <Button
            color="success"
            block
            disabled={!selectedLeagueOption || attaching}
            onClick={handleAttach}
          >
            {attaching ? "Adding..." : "Add"}
          </Button>
        </Col>
      </Row>

      <div className="mb-2">
        <div className="search-box me-2 d-inline-block w-100">
          <div className="position-relative">
            <Input
              type="text"
              placeholder="Search attached leagues"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <i className="bx bx-search-alt search-icon" />
          </div>
        </div>
      </div>

      {filteredLeagues.length === 0 ? (
        <div className="text-center py-4 text-muted">
          No leagues attached to this special market.
        </div>
      ) : (
        <div
          className="table-responsive"
          style={{ maxHeight: "400px", overflowY: "auto" }}
        >
          <BootstrapTable
            keyField="id"
            responsive
            bordered={false}
            striped={false}
            classes="table align-middle table-nowrap"
            headerWrapperClasses="table-light"
            columns={columns}
            data={filteredLeagues}
          />
        </div>
      )}
    </div>
  )
}
